import { Component, ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

// App-wide safety net: a render error anywhere below this used to white-screen
// the whole app. Now it shows the error (so it can be reported) + a reload.
// Inline styles on purpose — works even if styling/theme is the culprit.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error('App crashed:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: '#0b0b0f', color: '#e5e7eb', fontFamily: 'system-ui, sans-serif' }}>
          <div style={{ maxWidth: 600, width: '100%' }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 12 }}>
              The app hit an unexpected error. Reload to try again — if it keeps happening, copy the details below and send them over.
            </p>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#15151c', padding: 12, borderRadius: 8, maxHeight: 260, overflow: 'auto', margin: 0 }}>
              {String(this.state.error?.message || this.state.error)}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
            <button
              onClick={() => { this.setState({ error: null }); window.location.reload(); }}
              style={{ marginTop: 12, padding: '8px 16px', borderRadius: 8, background: '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
            >
              Reload app
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
